import { createApi } from '@reduxjs/toolkit/query/react';
import { S3_BUCKET_ACTIONS, S3_BUCKET_SERVICE } from 'utils/const';
import {
  getAllFilesFromS3,
  getFileFromS3,
  uploadFileToS3,
  cancelFileUploadToS3,
  downloadFileFromS3,
  copyFileToS3Folder,
  removeFileFromS3,
  getLastUpdatedFileInFolder,
  getFileVersionHistory,
  listFilesInS3Folder,
  deleteAllVersionsOfObject,
  deleteLatestVersionOfObject,
  downloadObjectVersion,
  publishToMipulse,
  publishFilesToMipulseUAT,
  uploadJsonToS3,
  readCsvInS3,
} from './s3BucketApis';

export const s3BucketApi = createApi({
  reducerPath: 's3BucketApi',
  baseQuery: async (args) => {
    const { method } = args;

    switch (method) {
      case S3_BUCKET_ACTIONS.LIST:
        return getAllFilesFromS3(args);

      case S3_BUCKET_ACTIONS.GET:
        return getFileFromS3(args);

      case S3_BUCKET_ACTIONS.PUT:
        return uploadFileToS3(args);

      case S3_BUCKET_ACTIONS.CANCEL:
        return cancelFileUploadToS3(args);

      case S3_BUCKET_ACTIONS.DOWNLOAD:
        return downloadFileFromS3(args);

      case S3_BUCKET_ACTIONS.COPY:
        return copyFileToS3Folder(args);

      case S3_BUCKET_ACTIONS.REMOVE:
        return removeFileFromS3(args);

      case S3_BUCKET_ACTIONS.UPLOAD_JSON:
        return uploadJsonToS3(args);

      case S3_BUCKET_SERVICE.PATHS.GET_LAST_UPDATED_FILE_IN_FOLDER:
        return getLastUpdatedFileInFolder(args);

      case S3_BUCKET_SERVICE.PATHS.GET_FILE_VERSION_HISTORY:
        return getFileVersionHistory(args);

      case S3_BUCKET_SERVICE.PATHS.LIST_FILES_IN_S3_FOLDER:
        return listFilesInS3Folder(args);

      case S3_BUCKET_SERVICE.PATHS.DELETE_ALL_VERSIONS_OF_OBJECT:
        return deleteAllVersionsOfObject(args);

      case S3_BUCKET_SERVICE.PATHS.DELETE_LATEST_VERSION_OF_OBJECT:
        return deleteLatestVersionOfObject(args);

      case S3_BUCKET_SERVICE.PATHS.DOWNLOAD_OBJECT_VERSION:
        return downloadObjectVersion(args);

      case S3_BUCKET_SERVICE.PATHS.PUBLISH_TO_MIPULSE:
        return publishToMipulse(args);

      case S3_BUCKET_SERVICE.PATHS.PUBLISH_FILES_TO_MIPULSE_UAT:
        return publishFilesToMipulseUAT(args);

      case S3_BUCKET_SERVICE.PATHS.READ_CSV_DATA:
        return readCsvInS3(args);

      default:
        return { error: 'method attribute is missing in the arguments!' };
    }
  },
  endpoints: (builder) => ({
    getAllFilesFromS3: builder.query({
      query: (args) => ({
        method: S3_BUCKET_ACTIONS.LIST,
        ...args,
      }),
    }),
    getFileFromS3: builder.query({
      query: (args) => ({
        method: S3_BUCKET_ACTIONS.GET,
        ...args,
      }),
    }),
    uploadFileToS3: builder.mutation({
      query: (args) => ({
        method: S3_BUCKET_ACTIONS.PUT,
        ...args,
      }),
    }),
    cancelFileUploadToS3: builder.mutation({
      query: (args) => ({
        method: S3_BUCKET_ACTIONS.CANCEL,
        ...args,
      }),
    }),
    downloadFileFromS3: builder.mutation({
      query: (args) => ({
        method: S3_BUCKET_ACTIONS.DOWNLOAD,
        ...args,
      }),
    }),
    copyFileToS3Folder: builder.mutation({
      query: (args) => ({
        method: S3_BUCKET_ACTIONS.COPY,
        ...args,
      }),
    }),
    removeFileFromS3: builder.mutation({
      query: (args) => ({
        method: S3_BUCKET_ACTIONS.REMOVE,
        ...args,
      }),
    }),
    uploadJsonToS3: builder.mutation({
      query: (args) => ({
        method: S3_BUCKET_ACTIONS.UPLOAD_JSON,
        ...args,
      }),
    }),
    getLastUpdatedFileInFolder: builder.query({
      query: (args) => ({
        method: S3_BUCKET_SERVICE.PATHS.GET_LAST_UPDATED_FILE_IN_FOLDER,
        ...args,
      }),
    }),
    getFileVersionHistory: builder.query({
      query: (args) => ({
        method: S3_BUCKET_SERVICE.PATHS.GET_FILE_VERSION_HISTORY,
        ...args,
      }),
      providesTags: (result, error, args) => [
        { type: 'FileVersionHistory', id: args.s3Folder },
      ],
    }),
    listFilesInS3Folder: builder.query({
      query: (args) => ({
        method: S3_BUCKET_SERVICE.PATHS.LIST_FILES_IN_S3_FOLDER,
        ...args,
      }),
    }),
    deleteAllVersionsOfObject: builder.mutation({
      query: (args) => ({
        method: S3_BUCKET_SERVICE.PATHS.DELETE_ALL_VERSIONS_OF_OBJECT,
        ...args,
      }),
      invalidatesTags: () => [{ type: 'FileVersionHistory' }],
    }),
    deleteLatestVersionOfObject: builder.mutation({
      query: (args) => ({
        method: S3_BUCKET_SERVICE.PATHS.DELETE_LATEST_VERSION_OF_OBJECT,
        ...args,
      }),
      invalidatesTags: () => [{ type: 'FileVersionHistory' }],
    }),
    downloadObjectVersion: builder.mutation({
      query: (args) => ({
        method: S3_BUCKET_SERVICE.PATHS.DOWNLOAD_OBJECT_VERSION,
        ...args,
      }),
    }),
    publishToMipulse: builder.mutation({
      query: (args) => ({
        method: S3_BUCKET_SERVICE.PATHS.PUBLISH_TO_MIPULSE,
        ...args,
      }),
    }),
    publishFilesToMipulseUAT: builder.mutation({
      query: (args) => ({
        method: S3_BUCKET_SERVICE.PATHS.PUBLISH_FILES_TO_MIPULSE_UAT,
        ...args,
      }),
    }),
    readCsvInS3: builder.query({
      query: (args) => ({
        method: S3_BUCKET_SERVICE.PATHS.READ_CSV_DATA,
        ...args,
      }),
    }),
  }),
});

export const {
  useLazyGetAllFilesFromS3Query,
  useGetFileFromS3Query,
  useLazyGetFileFromS3Query,
  useUploadFileToS3Mutation,
  useCancelFileUploadToS3Mutation,
  useDownloadFileFromS3Mutation,
  useCopyFileToS3FolderMutation,
  useRemoveFileFromS3Mutation,
  useUploadJsonToS3Mutation,
  useGetLastUpdatedFileInFolderQuery,
  useGetFileVersionHistoryQuery,
  useLazyGetFileVersionHistoryQuery,
  useListFilesInS3FolderQuery,
  useDeleteAllVersionsOfObjectMutation,
  useDeleteLatestVersionOfObjectMutation,
  useDownloadObjectVersionMutation,
  usePublishToMipulseMutation,
  usePublishFilesToMipulseUATMutation,
  useReadCsvInS3Query,
} = s3BucketApi;
